import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import ButtonWithLoading from "@/components/button-with-loading";
import { DatePicker } from "components/date-picker-2";
import { Form } from "react-crud-form";
import { FormLabel } from "components/form";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import React from "react";
import { Textarea } from "ui/components/textarea";
import { extensionSchema } from "./schema";
import HeaderPortal from "components/portal/header-portal";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateBillableExtension } from "../create/mutation";

const BillableExtensionForm = ({ billableId, handleClose }) => {
  const navigate = useNavigate(); // Add this line

  const createExtensionMutation = useCreateBillableExtension();

  const form = useForm({
    resolver: zodResolver(extensionSchema),
  });

  async function handleCreate(context) {
    const values = { ...context.values, billable: billableId };
    const response = await createExtensionMutation.mutateAsync({
      values,
    });
    navigate(-1);
  }

  return (
    <Form
      form={form}
      create={handleCreate}
      itemId={billableId || "+"}
      schema={extensionSchema}
    >
      {({ isCreating, createItem, isDirty }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(false);
              createItem().then(handleClose);
            }
          },
          { preventDefault: true },
        );

        useHotkeys(
          "ctrl+shift+s, cmd+shift+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(true);
              createItem().then(() => {
                if (!isNew) {
                  handleClose();
                }
              });
            }
          },
          { preventDefault: true },
        );
        return (
          <div className="space-y-6 max-w-md">
            <FormField
              control={form.control}
              name="billing_date"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="billable_extensions"
                    rule={"create:SC,AM"}
                  >
                    <FormLabel>Billing Date</FormLabel>
                    <FormControl>
                      <DatePicker {...field} />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="justification"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="billable_extensions"
                    rule={"create:SC,AM"}
                  >
                    <FormLabel>Justification</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="resize-none h-24 w-full"
                      />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />
            <HeaderPortal>
              <PermissionGate
                collection="billable_extensions"
                rule={"create:SC,AM"}
                fallback={
                  <div className="text-sm text-gray-500 italic">
                    You don't have permission to create extension in this
                    billable
                  </div>
                }
              >
                <ButtonWithLoading
                  variant="default"
                  isLoading={isCreating}
                  onClick={createItem}
                  disabled={!isDirty}
                >
                  Create
                </ButtonWithLoading>
              </PermissionGate>
            </HeaderPortal>
          </div>
        );
      }}
    </Form>
  );
};

export default BillableExtensionForm;
